import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { MenuItem } from '@material-ui/core';
import { FaChartLine } from 'react-icons/fa';
import * as Yup from 'yup';

import Paper from 'components/Paper';
import TextField from 'components/TextField';
import Button from 'components/Button';
import NaturezasActions from 'store/ducks/naturezas';

import {
  Container,
  WrapperBody,
  WrapperContent,
  WrapperInput,
  InputRow,
} from './styles';

// Validação com Yup
const validationSchema = Yup.object().shape({
  codigo: Yup.string().required('Código é obrigatório'),
  descricao: Yup.string().required('Descrição é obrigatória'),
  tipo: Yup.string().required('Tipo é obrigatório').oneOf(['Sintetica', 'Analitica'], 'Tipo deve ser Sintética ou Analítica'),
  classificacao: Yup.string().required('Classificação é obrigatória').oneOf(['Receita', 'Despesa'], 'Classificação deve ser Receita ou Despesa'),
});

function NaturezaForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const { naturezas, isLoading } = useSelector((state) => state.naturezas);
  const isEdit = !!id;
  const isView = window.location.pathname.includes('/view/');

  // Buscar natureza se estiver editando ou visualizando
  useEffect(() => {
    if ((isEdit || isView) && naturezas.length === 0) {
      dispatch(NaturezasActions.getNaturezasRequest());
    }
  }, [dispatch, isEdit, isView, naturezas.length]);

  const natureza = (isEdit || isView) ? naturezas.find((item) => item.id === id) : null;

  const formik = useFormik({
    initialValues: {
      codigo: natureza?.codigo || '',
      codigoPai: natureza?.codigoPai || '',
      descricao: natureza?.descricao || '',
      tipo: natureza?.tipo || 'Sintetica',
      classificacao: natureza?.classificacao || 'Receita',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const naturezaData = {
        ...values,
        id: natureza?.id,
      };

      const handleSuccess = () => {
        history.push('/financeiro/naturezas');
      };

      if (isEdit) {
        dispatch(
          NaturezasActions.updateNaturezaRequest(naturezaData, handleSuccess)
        );
      } else {
        dispatch(
          NaturezasActions.addNaturezaRequest(naturezaData, handleSuccess)
        );
      }
    },
  });

  useEffect(() => {
    if (natureza) {
      formik.setValues({
        codigo: natureza.codigo || '',
        codigoPai: natureza.codigoPai || '',
        descricao: natureza.descricao || '',
        tipo: natureza.tipo || 'Sintetica',
        classificacao: natureza.classificacao || 'Receita',
      });
    }
  }, [natureza]);

  return (
    <Container>
      <Paper
        title={isView ? 'VISUALIZAR NATUREZA' : isEdit ? 'EDITAR NATUREZA' : 'NOVA NATUREZA'}
        icon={<FaChartLine size={24} style={{ color: '#122d58', marginRight: '25px' }} />}
        rightContent={
          !isView && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Button
                label="Cancelar"
                onClick={() => history.push('/financeiro/naturezas')}
                type="button"
                width="120px"
              />
              <Button
                label={isEdit ? 'Atualizar' : 'Salvar'}
                type="button"
                width="120px"
                onClick={() => {
                  formik.handleSubmit();
                }}
                disabled={isLoading}
              />
            </div>
          )
        }
      >
        <form onSubmit={formik.handleSubmit}>
          <WrapperBody>
            <WrapperContent>
              <InputRow>
                <WrapperInput>
                  <TextField
                    label="Código *"
                    name="codigo"
                    value={formik.values.codigo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.codigo && Boolean(formik.errors.codigo)}
                    helperText={formik.touched.codigo && formik.errors.codigo}
                    fullWidth
                    color="secondary"
                    disabled={isView}
                  />
                </WrapperInput>
                <WrapperInput>
                  <TextField
                    label="Código Pai"
                    name="codigoPai"
                    value={formik.values.codigoPai}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    fullWidth
                    color="secondary"
                    disabled={isView}
                    helperText="Código da natureza pai (para hierarquia)"
                  />
                </WrapperInput>
              </InputRow>

              <InputRow>
                <WrapperInput>
                  <TextField
                    label="Descrição *"
                    name="descricao"
                    value={formik.values.descricao}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.descricao && Boolean(formik.errors.descricao)}
                    helperText={formik.touched.descricao && formik.errors.descricao}
                    fullWidth
                    color="secondary"
                    disabled={isView}
                  />
                </WrapperInput>
              </InputRow>

              <InputRow>
                <WrapperInput>
                  <TextField
                    label="Tipo *"
                    name="tipo"
                    select
                    value={formik.values.tipo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.tipo && Boolean(formik.errors.tipo)}
                    helperText={formik.touched.tipo && formik.errors.tipo}
                    fullWidth
                    color="secondary"
                    disabled={isView}
                  >
                    <MenuItem value="Sintetica">Sintética</MenuItem>
                    <MenuItem value="Analitica">Analítica</MenuItem>
                  </TextField>
                </WrapperInput>
                <WrapperInput>
                  <TextField
                    label="Classificação *"
                    name="classificacao"
                    select
                    value={formik.values.classificacao}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.classificacao && Boolean(formik.errors.classificacao)}
                    helperText={formik.touched.classificacao && formik.errors.classificacao}
                    fullWidth
                    color="secondary"
                    disabled={isView}
                  >
                    <MenuItem value="Receita">Receita</MenuItem>
                    <MenuItem value="Despesa">Despesa</MenuItem>
                  </TextField>
                </WrapperInput>
              </InputRow>
            </WrapperContent>
          </WrapperBody>
        </form>
      </Paper>
    </Container>
  );
}

export default NaturezaForm;

